import { Button } from '@grofers/react';
import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Heading, Loader, Table } from '../../../UiKit';
import { useGetUserPermissions, useGetUserPermissionsHistory } from '../apis';
import { getFullDate } from '../../../components/commons/CommonMethods';
import { grey } from 'sushiweb/tokens/color';
import Spinner from 'sushiweb/Spinner/Spinner';
import { Star } from '../../../UiKit/Star/Star';
import { toUpper } from 'lodash-es';

const ViewPermissions = () => {
  const form = useForm();
  const { register, handleSubmit, formState, watch } = form;
  const { errors } = formState;
  const watchEmail = watch('email');
  const {
    data: { permissions: dashboards = [] } = {},
    refetch,
    isFetching,
    isSuccess: getPermissionsSuccess,
  } = useGetUserPermissions(watchEmail);

  const {
    isLoading: permissionsHistoryLoading,
    data: { permission_update_events: permissionUpdateEvents = [] } = {},
    refetch: refetchPermissionsHistory,
    error: permissionsHistoryError,
    isFetching: permissionsHistoryFetching,
    isSuccess: getPermissionsHistorySuccess,
  } = useGetUserPermissionsHistory(watchEmail);

  const getOrgWiseRoles = useCallback(
    (dashboardData) => {
      let updated_data = {};
      if (!dashboardData || !dashboardData.permissions) return updated_data;
      for (
        let permission = 0;
        permission < dashboardData.permissions.length;
        permission++
      ) {
        const org_ids = dashboardData.permissions[permission].org_ids;
        for (let org = 0; org < org_ids.length; org++) {
          const org_id = org_ids[org];
          const added_roles = updated_data[org_id] || [];
          added_roles.push(dashboardData.permissions[permission].name);
          updated_data = {
            ...updated_data,
            [org_ids[org]]: added_roles,
          };
        }
      }

      return updated_data;
    },
    [dashboards],
  );

  const getPermissionViewTableData = useCallback(
    (dashboardData) => {
      const permissionData = getOrgWiseRoles(dashboardData) || {};
      const orgs = Object.keys(permissionData);
      return {
        headings: [
          { name: 'Org', key: 1 },
          { name: 'Permissions', key: 2 },
        ],
        rows: orgs.map((org) => {
          return {
            1: {
              title: (
                <div className='tw-flex tw-flex-wrap tw-gap-4'>
                  <div className='tw-px-6 tw-py-3 tw-bg-blue-100  tw-rounded-full tw-font-regular tw-text-300'>
                    {org}
                  </div>
                </div>
              ),
            },
            2: {
              title: (
                <div className='tw-flex tw-flex-wrap tw-gap-4'>
                  {permissionData[org].map((role) => {
                    return (
                      <div className='tw-px-6 tw-py-3 tw-bg-blue-100  tw-rounded-full tw-font-regular tw-text-300'>
                        {role}
                      </div>
                    );
                  })}
                </div>
              ),
            },
          };
        }),
      };
    },
    [dashboards],
  );

  const permissionHistoryTableData = useMemo(() => {
    return {
      headings: [
        // { name: 'Dashboard', key: 1 },
        { name: 'Action', key: 2 },
        { name: 'Actioner', key: 3 },
        { name: 'Date', key: 4 },
        { name: 'Reason', key: 5 },
        { name: 'Permission', key: 6 },
        { name: 'Orgs', key: 7 },
      ],
      rows: permissionUpdateEvents.map((updateEvents) => ({
        // 1: { title: '' },
        2: {
          title: (
            <div
              style={{
                color: `${
                  updateEvents?.action === 'revoke' ? '#FF0000' : '#318616'
                }`,
                backgroundColor: `${
                  updateEvents?.action === 'revoke' ? '#fff0f0' : '#edf5eb'
                }`,
                padding: '0.2rem 1rem',
                borderRadius: '0.5rem',
              }}
            >
              {toUpper(updateEvents.action)}
            </div>
          ),
        },
        3: {
          title: <div data-dd-privacy='mask'>{updateEvents.creator_email}</div>,
        },
        4: { title: getFullDate(updateEvents.epoch * 1000) },
        5: { title: updateEvents.reason || '-' },
        6: {
          title: (
            <div className='tw-flex tw-flex-wrap tw-gap-4'>
              {updateEvents.permissions.map((perm) => {
                return (
                  <div className='tw-px-6 tw-py-3 tw-bg-blue-100  tw-rounded-full tw-font-regular tw-text-300'>
                    {perm.name}
                  </div>
                );
              })}
            </div>
          ),
        },
        7: {
          title: (
            <div className='tw-flex tw-flex-wrap tw-gap-4'>
              {updateEvents.orgs.map((org) => {
                return (
                  <div className='tw-px-6 tw-py-3 tw-bg-blue-100  tw-rounded-full tw-font-regular tw-text-300'>
                    {org}
                  </div>
                );
              })}
            </div>
          ),
        },
      })),
    };
  }, [permissionUpdateEvents]);

  const onSubmit = () => {
    refetch();
    refetchPermissionsHistory();
  };

  return (
    <div className='tw-w-full tw-flex tw-flex-col'>
      <form
        className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-5 tw-bg-white tw-p-[2rem] tw-rounded-[1rem]'
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className='tw-w-full tw-color-[#1F1F1F] tw-font-bold tw-text-[17px]'>
          Email ID <Star icon='*' />
        </div>
        <div className='tw-w-full tw-flex tw-h-max'>
          <label htmlFor='email'></label>
          <div className='tw-flex tw-flex-col tw-w-[80%]'>
            <input
              id='email'
              name='email'
              placeholder='Please enter email id'
              className={`tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0  focus:tw-border-none focus:tw-ring-[1.5px]
              ${
                errors.email?.message
                  ? 'focus:tw-ring-[#BF2938] focus:tw-border-RED'
                  : 'focus:tw-ring-green-500 focus:tw-border-green-500'
              }  tw-border tw-border-solid tw-border-[#cfcfcf] tw-mr-[1.5rem]`}
              {...register('email', {
                pattern: {
                  value: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/,
                  message: 'Invalid email id',
                },
              })}
              autoComplete='off'
              data-dd-privacy='mask'
              required
            />
            <p className='tw-mt-1 tw-text-[#BF2938]'>{errors.email?.message}</p>
          </div>
          <Button variant={'fill'} className='tw-h-16 tw-w-[20%]'>
            {isFetching || permissionsHistoryFetching ? (
              <Spinner size='small' color={`${grey.z300}`} />
            ) : (
              'View Permissions'
            )}
          </Button>
        </div>
      </form>
      <div className='mt20' />
      {(getPermissionsHistorySuccess || getPermissionsSuccess) && (
        <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-5 tw-bg-white tw-p-[2rem] tw-rounded-[1rem]'>
          {getPermissionsSuccess && (
            <div className='tw-flex tw-flex-col tw-mb-6'>
              <Heading size='20px' title='Dashboard Permissions' />
              {dashboards
                ? dashboards.map((dashboardData) => (
                    <Table
                      tableData={getPermissionViewTableData(dashboardData)}
                      message='user currently does not have any permissions'
                    />
                  ))
                : null}
            </div>
          )}
          {getPermissionsHistorySuccess && (
            <div
              className='tw-flex tw-flex-col'
              css={`
                & td {
                  max-width: 37rem;
                  min-width: 12rem;
                }
              `}
            >
              <Heading size='20px' title='Permissions History' />
              <Table
                tableData={permissionHistoryTableData}
                message='User does not have any permission history'
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewPermissions;
